import type { StyleProp, ViewStyle } from "react-native";
import { View } from "react-native";
import { useTranslation } from "react-i18next";

import type { UiTheme } from "../theme";
import { Icon } from "../Icon/Icon";
import { IconButton } from "../primitives/IconButton";
import { RowAction } from "../primitives/RowAction";
import type { LibraryItem } from "./useLibrary";

// The actions row of LibraryItemCard, split out to keep that file under the
// 500-line cap (it was 424 and every row added here pushed it further). Pure
// move: same elements, same order, same props.
//
// Every handler is optional, and its presence IS the gate — a host without a
// capability (no logger on web → no Avvia) simply doesn't pass it, and the
// row disappears. That convention is why this component takes handlers rather
// than a list of enabled action names.
//
// `styles.actions` stays owned by the parent and arrives as `style`: it
// encodes a gap tuned against the primitives' hitSlop, and a second
// definition here could drift from it silently.
//
// ⚠️ That inverts the convention the primitives in ../primitives use, where
// the component owns a base style and `style` is only a caller's override.
// It is deliberate while this has exactly ONE caller: there is no base to
// own that the parent isn't already defining. If a second caller appears,
// flip it — move `actions` into a makeStyles here (the TierOneTrendRow
// shape) and delete it from LibraryItemCard's makeStyles — rather than
// letting the second caller hand in its own copy of the gap.
export type LibraryItemCardActionsProps = {
  theme: UiTheme;
  item: LibraryItem;
  /** Row name, spoken by the pencil/trash hints that have no visible label. */
  name: string;
  style?: StyleProp<ViewStyle>;

  /** Inline preview toggle state — drives both the label and the chevron. */
  open: boolean;
  onTogglePreview: () => void;
  previewLabel: string;

  /** Personal plans on hosts with a logger (mobile) — starts the session. */
  onStart?: () => void;
  starting?: boolean;

  onFollow?: () => void;
  following?: boolean;

  onPublish?: () => void;
  onUnfollow?: () => void;
  /** True while a publish/unfollow write is in flight. */
  statusBusy?: boolean;

  /** Already carries the cloning / already-cloned state as text. */
  cloneLabel: string;
  onClone: (item: LibraryItem) => void;
  cloning: boolean;
  cloneBusy: boolean;
  alreadyCloned: boolean;

  onEdit?: () => void;

  /** Presence gates the trash; the press itself goes through onDeletePress. */
  onDelete?: () => void;
  /**
   * Kept in the parent on purpose: it closes over the armed-confirm state and
   * the host/inline seam. Rebuilding it here from a passed-down `confirmDelete`
   * would reintroduce the stale-`confirming` bug it exists to avoid.
   */
  onDeletePress: () => void;
  /** Armed state — read directly, since it picks WHICH element renders. */
  confirming: boolean;
};

export function LibraryItemCardActions({
  theme,
  item,
  name,
  style,
  open,
  onTogglePreview,
  previewLabel,
  onStart,
  starting = false,
  onFollow,
  following = false,
  onPublish,
  onUnfollow,
  statusBusy = false,
  cloneLabel,
  onClone,
  cloning,
  cloneBusy,
  alreadyCloned,
  onEdit,
  onDelete,
  onDeletePress,
  confirming,
}: LibraryItemCardActionsProps) {
  const { t } = useTranslation();

  return (
    <View style={style}>
      <RowAction
        theme={theme}
        label={previewLabel}
        onPress={onTogglePreview}
        expanded={open}
        trailing={
          <Icon
            name={open ? "chevron-up" : "chevron-down"}
            size={theme.typography.sizes.sm}
            color={theme.palette.ink2}
          />
        }
      />
      {onStart ? (
        <RowAction
          theme={theme}
          label={t("plans.start", { defaultValue: "Start" })}
          onPress={onStart}
          disabled={starting}
          accessibilityLabel={t("mobile.plan.startPlanA11y", {
            name,
            defaultValue: "Start {{name}}",
          })}
        />
      ) : null}
      {onFollow ? (
        <RowAction
          theme={theme}
          label={
            following
              ? t("library.following", { defaultValue: "Following…" })
              : t("program.follow", { defaultValue: "Follow" })
          }
          onPress={onFollow}
          disabled={following}
          accessibilityHint={t("program.followHint", {
            defaultValue: "Follow this program (it shows up in In progress)",
          })}
        />
      ) : null}
      {onPublish ? (
        <RowAction
          theme={theme}
          label={t("program.publish", { defaultValue: "Publish" })}
          onPress={onPublish}
          disabled={statusBusy}
          accessibilityHint={t("program.publishHint", {
            defaultValue: "Make this program available to follow",
          })}
        />
      ) : null}
      {onUnfollow ? (
        <RowAction
          theme={theme}
          label={t("program.unfollow", { defaultValue: "Unfollow" })}
          onPress={onUnfollow}
          disabled={statusBusy}
          accessibilityHint={t("mobile.plan.programUnfollowHint", {
            defaultValue: "Stop following this program (it stays in My programs)",
          })}
        />
      ) : null}
      <RowAction
        theme={theme}
        label={cloneLabel}
        onPress={() => onClone(item)}
        // Busy/cloned state is never colour-alone: it lands in a11y
        // state and, for cloning/already-cloned, in the label itself.
        disabled={cloning || cloneBusy || alreadyCloned}
      />
      {/* Edit/Delete are icons (pencil/trash — Antenore 2026-07-04, HR-7):
          with up to 5 actions the all-text row barely fit a large phone.
          The semantic/state-bearing actions above stay text (their labels
          carry state — "Cloning…" — which an icon would reduce to
          opacity-alone); these two are universally glyphable. */}
      {onEdit ? (
        <IconButton
          theme={theme}
          name="edit"
          onPress={onEdit}
          accessibilityLabel={t("program.edit", { defaultValue: "Edit" })}
          // The name disambiguates WHICH row's pencil this is — with no
          // visible text label left, a screen reader in a long list has
          // no other anchor (HR-7 a11y review). Programs keep the richer
          // pre-existing phrase; plans get the bare name.
          accessibilityHint={
            item.kind === "program" ? t("program.editing", { name }) : name
          }
        />
      ) : null}
      {onDelete ? (
        confirming ? (
          // The inline confirm step stays a destructive TEXT action — the
          // irreversible tap must never be icon-only.
          <RowAction
            theme={theme}
            label={t("program.deleteConfirm", { defaultValue: "Confirm?" })}
            onPress={onDeletePress}
            destructive
          />
        ) : (
          <IconButton
            theme={theme}
            name="trash"
            variant="destructive"
            onPress={onDeletePress}
            accessibilityLabel={t("workout.delete", {
              defaultValue: "Delete",
            })}
            // Same disambiguation as the pencil: the spoken hint names
            // the row, since the icon carries no visible text.
            accessibilityHint={name}
          />
        )
      ) : null}
    </View>
  );
}
